import {Box, Button, createStyles, Divider, Group, Loader, Stack, Text, Title} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {getErrorMessageForAdditionalColumns} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  NdcSpreadsheetInterpretation,
  NdcStartingType,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import Antibody from 'antibodies/visualization/Antibody';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {SPREADSHEET_MIME_TYPE, TaskState} from 'bulk-import/SpreadsheetBulkImportConstants';
import notifications from 'notifications';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';

const useStyles = createStyles((theme) => ({
  spreadsheetBannerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
  },
  spreadsheetChooser: {
    '&.drop-help': {
      margin: 0,
    },
  },
  templateDownloadSection: {
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
  },
  templateDownloadText: {
    flex: 1,
  },
}));

export interface UploadSpreadsheetPaneProps {
  startingType: NdcStartingType;
  spreadsheetFile: File | null;
  setSpreadsheetFile: (spreadsheetFile: File) => void;
  setSpreadsheetURL: (spreadsheetURL: string) => void;
  clearSpreadsheet: () => void;
  spreadsheetInterpretation: NdcSpreadsheetInterpretation | null;
  columnInferenceErrorMessage: string | null;
  selectedAntibodyFormat: AntibodyFormat;
}

export default function UploadSpreadsheetPane({
  startingType,
  spreadsheetFile,
  setSpreadsheetFile,
  setSpreadsheetURL,
  clearSpreadsheet,
  spreadsheetInterpretation,
  columnInferenceErrorMessage,
  selectedAntibodyFormat,
}: UploadSpreadsheetPaneProps): JSX.Element {
  const {classes} = useStyles();

  const [uploadState, setUploadState] = useState<TaskState | null>(null);

  const handleFileSelect = async (file: File): Promise<void> => {
    setUploadState(TaskState.STARTED);
    try {
      setSpreadsheetFile(file);
      const url = await ImportUtil.upload(file);
      assert(url != null, 'Failed to get a valid S3 URL');
      setSpreadsheetURL(url);
      setUploadState(TaskState.COMPLETED);
    } catch (err) {
      setUploadState(TaskState.FAILED);
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
  };

  const handleClearSpreadsheet = (): void => {
    clearSpreadsheet();
    setUploadState(null);
  };

  const additionalColumnsErrorMessage: string | null = useMemo(
    () =>
      spreadsheetInterpretation == null
        ? null
        : getErrorMessageForAdditionalColumns(spreadsheetInterpretation, startingType),
    [spreadsheetInterpretation, startingType]
  );

  return (
    <div>
      <Group spacing="md" align="center" noWrap={true} mb="lg">
        <Box
          w="50px"
          h="50px"
          px="5px"
          py="7px"
          bg="gray.0"
          sx={(theme) => ({
            borderRadius: theme.radius.md,
            border: `1px solid ${theme.colors.gray[3]}`,
          })}
        >
          <Antibody format={selectedAntibodyFormat} />
        </Box>
        {/* We need to re-enable wrapping since Group noWrap gets inherited */}
        <Stack spacing={0} style={{flex: 1, minWidth: 0, whiteSpace: 'normal'}}>
          <Title order={5} fw={600}>
            Antibody {startingType.toString().toLowerCase()}
          </Title>
          <Text size="sm">
            Upload a spreadsheet containing your antibody {startingType.toString().toLowerCase()}
          </Text>
        </Stack>
      </Group>

      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="Ensure the spreadsheet is formatted with a column for each antibody domain"
        supportingInfo={
          <div className={classes.spreadsheetBannerContent}>
            <p>
              For each domain column, enter either raw sequence bases/residues or an existing Benchling entity
              name or registry ID. Each domain column should use one type consistently.
            </p>
            <img
              src={
                startingType === NdcStartingType.DOMAINS
                  ? '/static/img/antibody-spreadsheet-import-domains-example.svg'
                  : startingType === NdcStartingType.PAIRS
                    ? '/static/img/antibody-spreadsheet-import-pairs-example.svg'
                    : '/static/img/404-not-found-2025.svg' // TODO(BNCH-142550): Add example image for chains
              }
            />
          </div>
        }
        action={{
          actionType: InlineBannerActionType.ACCORDION,
          accordionLabels: {moreLabel: 'Show details', lessLabel: 'Hide details'},
          rightAlign: true,
          initialShowMore: true,
        }}
      />

      <Divider my="lg" />

      <div className={classes.templateDownloadSection}>
        <div className={classes.templateDownloadText}>
          <Text weight={600} size="sm">
            Don't have a spreadsheet ready?
          </Text>
          <Text size="sm" color="dimmed">
            Download a template with columns that match your selected format
          </Text>
        </div>
        <Button
          variant="light"
          size="md"
          onClick={() => {
            // TODO(BNCH-142552): Implement template download
          }}
        >
          Download template
        </Button>
      </div>

      <Text size="sm" mb="lg">
        The maximum number of rows you can import is{' '}
        {BenchlingConfig.ANTIBODY_NESTED_DATA_CREATION_MAX_ANTIBODIES_PER_RUN}.
      </Text>

      {spreadsheetFile ? (
        <FileList>
          <FileRow
            file={spreadsheetFile}
            onRequestRemove={handleClearSpreadsheet}
            additionalContent={uploadState === TaskState.STARTED ? <Loader size="sm" /> : undefined}
            disabled={uploadState === TaskState.STARTED}
          />
        </FileList>
      ) : (
        <FileChooser
          accept={SPREADSHEET_MIME_TYPE}
          multiple={false}
          onSelectFiles={handleFileSelect}
          className={classes.spreadsheetChooser}
        />
      )}

      {uploadState === TaskState.FAILED && (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Error uploading spreadsheet" />
      )}

      {(columnInferenceErrorMessage != null || additionalColumnsErrorMessage != null) && (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={columnInferenceErrorMessage ?? additionalColumnsErrorMessage}
        />
      )}
    </div>
  );
}
